import type { LoaderFunctionArgs } from '~/types/route-args';
import { shouldRevalidateLeaf } from '~/modules/root/util/should-revalidate-leaf';
import React from 'react';
import PaginatedArticles from '@modules/articles/component/paginated-articles/PaginatedArticles';

import { useLoaderData } from 'react-router';
import FeaturedCarousel from '@modules/articles/component/featured-carousel/FeaturedCarousel';
import { fillExtraInfoForArticleCategory } from '@modules/articles/util/fill-company-admin.server';
import { adRequest } from '@modules/adverts/api';
import { response404 } from '~/modules/root/graphql/assertResponse.server';
import { getPaginationOpts } from '~/modules/root/util/get-pagination-opts.server';
import DirectoryHeader from '~/modules/articles/component/directory-header/DirectoryHeader';
import type { loader as archizeenRootLoader } from '~/routes/_app.articles';
import PageSections from '~/modules/professional/component/page-layout/PageSections';
import { shuffleArray } from '~/utils/arrayHelper';
import { mapAdDataToFeatureSlide } from '@modules/articles/util/map-ad-data-to-feature-slide';
import { useLocationMemo } from '@modules/adverts/hook/use-location-memo';
import { getPreloadImageLink } from '~/modules/images';
import {
    createMetaDescription,
    createOGTags,
    getCanonicalHreflang,
    mergeMeta,
} from '~/modules/seo';
import { createMetaTitle } from '~/modules/seo';
import type { APHandle } from '~/modules/root';
import { getArticleCategoryItems } from '~/modules/articles/api/get-article-category-items.server';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { getArticleNavigationItems } from '~/modules/articles/api/get-archizeen-common.server';
import { TOP_ARTICLES_COUNT } from '~/modules/articles/api/get-article-directory.server';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';
import { ArticleCategoryRevamp } from '@modules/articles-revamp/component/ArticleCategoryRevamp';
import { isArticlesRevampEnabled } from '@modules/articles-revamp/util/is-articles-revamp-enabled.server';
import { getRecentMagazineTiles } from '~/modules/magazine/api/get-recent-magazines.server';
import { buildArticlesFilterState } from '@modules/articles-revamp/api/build-articles-filter-state.server';
import { alsoInCategoryId } from '@modules/articles-revamp/util/also-in-options';
import {
    ARTICLE_FACET_PARAMS,
    ALSO_IN_FILTER_KEY,
} from '@modules-rocco/articles/config/article-facets';

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { data } = props;

        const title = createMetaTitle(
            props,
            [
                data?.localisation?.Title,
                data?.localisation?.DirectoryHeader?.Title,
            ],
            true,
            data?.pagination?.page,
        );

        const description = createMetaDescription([
            data?.localisation?.Description,
            `Learn about ${data?.localisation?.Title} through our blog of over 200 articles from expert writers and industry professionals.`,
        ]);

        const bannerImage =
            data?.articleCategory?.topReadArticles &&
            data?.articleCategory?.topReadArticles.length > 0
                ? data.articleCategory.topReadArticles[0]?.image.src
                : undefined;

        const preloadImageLinks = bannerImage
            ? data?.deviceType === 'mobile'
                ? getPreloadImageLink(undefined, bannerImage)
                : getPreloadImageLink(bannerImage)
            : [];

        return [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                meta: props,
                title,
                description,
            }),
            ...getCanonicalHreflang(props),
            ...preloadImageLinks,
        ];
    },
);

export const handle: APHandle = {
    isCategoryPage: true,
    hideContactArchiPro: false,
    fadeTopNavAfterPosition: 200,
    // Transparent header with the logomark over the full-bleed masthead,
    // mirroring the project and professional detail pages. Loader data rather
    // than the flag directly, so the legacy page is untouched when it is off.
    header: (loaderData) =>
        (loaderData as { showRevamp?: boolean } | undefined)?.showRevamp
            ? { headerVariant: 'transparent', showLogomark: true }
            : undefined,
};

export const loader = async (args: LoaderFunctionArgs) => {
    const { context, request } = args;
    const { device, user } = context;
    const requestUrl = request.url;
    const defaultPageSize = 36;

    // The cross-category facet travels as a URL segment, and only the nav tree
    // can say which category that is — so the nav is fetched first when the
    // facet is active, and alongside everything else when it is not.
    const alsoInSegment = new URL(request.url).searchParams.get(
        ALSO_IN_FILTER_KEY,
    );
    const navItemsFirst = alsoInSegment
        ? await getArticleNavigationItems(context)
        : undefined;
    const alsoInCategoryID = navItemsFirst
        ? alsoInCategoryId(
              navItemsFirst,
              new URL(request.url).pathname,
              alsoInSegment,
          )
        : undefined;

    const [articleCategory, localisation, bannerAds, navItems] =
        await Promise.all([
            getArticleCategoryItems({
                request: request,
                ...context,
                alsoInCategoryID,
            }),
            getPageLocalisation(args),
            adRequest({
                type: 'ArchizeenBannerPlacement',
                requestUrl,
                ...args.context,
            }),
            navItemsFirst ?? getArticleNavigationItems(context),
        ]);

    // An empty listing means one of two different things now that the revamp
    // has facets: a category that does not exist, or filters that happen to
    // match nothing. Only the first is a 404 — 404ing the second would tell the
    // reader the page moved and throw away the filters they had just set.
    const hasActiveFacets = ARTICLE_FACET_PARAMS.some((param) =>
        new URL(request.url).searchParams.has(param),
    );
    if (
        !articleCategory ||
        (!hasActiveFacets && !articleCategory.articles.length)
    ) {
        throw response404();
    }

    fillExtraInfoForArticleCategory(user, articleCategory);

    const pagination = getPaginationOpts({
        request,
        device,
        defaultPageSize,
        totalCount: articleCategory?.totalCount || 0,
    });

    // Only fetched behind the gate, so the legacy page costs nothing extra.
    const showRevamp = isArticlesRevampEnabled(context, request);
    const magazineTiles = showRevamp
        ? await getRecentMagazineTiles({ ...context, limit: 10 })
        : undefined;

    const bannerAdsShuffled = bannerAds ? shuffleArray(bannerAds) : [];
    const featuredSlides = bannerAdsShuffled?.length
        ? bannerAdsShuffled
              .slice(0, TOP_ARTICLES_COUNT)
              .map(mapAdDataToFeatureSlide)
        : (articleCategory?.topReadArticles ?? []);

    return {
        deviceType: context.device.type,
        articleCategory,
        localisation,
        bannerAds: bannerAdsShuffled,
        pagination,
        navItems,
        featuredSlides,
        showRevamp,
        editions: magazineTiles ?? [],
        // The listing's own facet states. Built from the URL only — the
        // "Published" presets are a fixed vocabulary, so nothing is aggregated.
        filterStates: showRevamp
            ? {
                  ...buildArticlesFilterState({
                      context,
                      request,
                      navItems: navItems ?? [],
                  }),
                  totalCount: articleCategory.totalCount ?? 0,
              }
            : undefined,
    };
};

const ArchizeenCategoryPage = () => {
    const {
        articleCategory,
        pagination,
        localisation,
        navItems,
        featuredSlides,
        showRevamp,
        editions,
        filterStates,
    } = useLoaderData<typeof loader>();
    const { topReadArticles: _, ...rest } = articleCategory;
    const { common } = useLoaderData<typeof archizeenRootLoader>();

    // Keep the first value returned by loader to avoid changing when loader revalidate
    const featuredSlidesMemoized = useLocationMemo(featuredSlides);
    const showFeaturedCarousel =
        !articleCategory.useFilmsCategoryLayout &&
        featuredSlidesMemoized.length > 0;

    const { setCategory } = useCurrentCategory();

    // Set the current category for header navigation
    React.useEffect(() => {
        setCategory('Archizeen');
        return () => setCategory(null);
    }, [setCategory]);

    // Gate sits below the hooks so both paths run the same hook order.
    if (showRevamp && filterStates) {
        return (
            <ArticleCategoryRevamp
                articles={rest.articles}
                totalCount={rest.totalCount ?? rest.articles.length}
                pageSize={rest.pageSize}
                navItems={navItems ?? []}
                title={localisation?.DirectoryHeader?.Title ?? undefined}
                heroImage={articleCategory.topReadArticles?.[0]?.image?.src}
                editions={editions}
                filterStates={filterStates}
            />
        );
    }

    return (
        <>
            <DirectoryHeader
                navItems={navItems ?? []}
                content={
                    localisation?.DirectoryHeader?.ContentTop ||
                    common?.contentTop
                }
                title={localisation?.DirectoryHeader?.Title}
            />
            <PageSections>
                {showFeaturedCarousel && (
                    <FeaturedCarousel
                        featuredSlides={featuredSlidesMemoized}
                        imageLazyLoading={'eager'}
                    />
                )}
                <PaginatedArticles {...rest} {...pagination} />
            </PageSections>
        </>
    );
};

export const shouldRevalidate = shouldRevalidateLeaf;
export default ArchizeenCategoryPage;
